import axios from "./axiosInstance";
import config from "./config";
import {
  lookupPublicMemberByEmail,
  verifyPublicMemberByEmail,
  MemberLookupResult as SharedLookupResult,
  MemberVerifyResult as SharedVerifyResult,
} from "./publicMemberLookupAPI";

const BASE_URL = config.BASE_URL;

export interface PublicEvent {
  id: string;
  name: string;
  description: string;
  dates: { date: string; start_time: string; end_time: string; location: string }[];
  pricing_options: { label: string; amount: number }[];
  max_registrations: number | null;
  current_registrations: number;
  cover_image_url: string | null;
  status: string;
  visibility: string;
  members_only?: boolean;
  waitlist_enabled: boolean;
  allow_other_gender?: boolean;
  restriction_rules?: any;
  category?: { id: string; name: string; color: string; icon: string } | null;
}

export interface GuestEventRegistrationData {
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  selected_dates?: string[];
  pricing_option?: { label: string; amount: number };
  notes?: string;
  waiver_signatures?: any[];
  membership_id?: string;
  // Proof from verify-member that the caller IS this membership (see
  // publicMemberLookupAPI.MemberLookupResult.data.member_token).
  member_token?: string;
  // The time slot booked and the add-ons picked, by id (PM 4e4384e4). The
  // server prices the add-ons; no amount is ever sent.
  session_id?: string;
  addons?: string[];
}

export interface BatchParticipant {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  athlete_id?: string;
}

export interface BatchGuestEventRegistrationData {
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  participants: BatchParticipant[];
  pricing_option?: { label: string; amount: number };
  selected_dates?: string[];
  notes?: string;
  waiver_signatures?: any[];
  membership_id?: string;
  // Proof from verify-member that the caller IS this membership (see
  // publicMemberLookupAPI.MemberLookupResult.data.member_token).
  member_token?: string;
}

// Member-identity types and calls now live in ./publicMemberLookupAPI —
// they were byte-identical across camps, clinics and events, wrapped around
// a password-authenticated security boundary. Re-exported here so existing
// imports keep working.
export type {
  MemberLookupAthlete,
  MemberLookupResult,
  MemberVerifyResult,
} from "./publicMemberLookupAPI";

export const lookupMemberByEmail = (
  tenantId: string,
  email: string
): Promise<SharedLookupResult> =>
  lookupPublicMemberByEmail("events", tenantId, email);

export const verifyMemberByEmail = (
  tenantId: string,
  email: string,
  password: string
): Promise<SharedVerifyResult> =>
  verifyPublicMemberByEmail("events", tenantId, email, password);




// Get all public events for a tenant (no auth required)
export const getPublicEvents = async (tenantId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/public/events`, {
      headers: {
        "x-tenant-id": tenantId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get a single public event by ID (no auth required)
export const getPublicEventById = async (tenantId: string, eventId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/public/events/${eventId}`, {
      headers: {
        "x-tenant-id": tenantId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};



// Register a guest for an event (no auth required)
export const registerGuestForEvent = async (
  tenantId: string,
  eventId: string,
  data: GuestEventRegistrationData
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/public/events/${eventId}/register`,
      data,
      {
        headers: {
          "x-tenant-id": tenantId,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Batch register multiple guests for an event (no auth required)
export const batchRegisterGuestForEvent = async (
  tenantId: string,
  eventId: string,
  data: BatchGuestEventRegistrationData
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/public/events/${eventId}/batch-register`,
      data,
      {
        headers: {
          "x-tenant-id": tenantId,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
